import { useEffect, useState } from 'react';
import { Box, Collapse, List, ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export interface NodeViewProps {
    name: string;
    description?: string;
    icon?: React.ReactNode;
    actions?: React.ReactNode;
    children?: React.ReactNode;
    depth?: number;
    defaultOpen?: boolean;
    forceOpen?: boolean;
    selected?: boolean;
    onClick?: () => void;
    onDrop?: () => void;
}

export default function NodeView({
    name,
    description,
    icon,
    actions,
    children,
    depth = 0,
    defaultOpen = false,
    forceOpen,
    selected,
    onClick = () => {},
    onDrop,
}: NodeViewProps) {
    const [open, setOpen] = useState(defaultOpen || (forceOpen ?? false));

    useEffect(() => {
        if (forceOpen) setOpen(true);
    }, [forceOpen]);
    const [isDragOver, setIsDragOver] = useState(false);
    const hasChildren = Boolean(children);

    return (
        <Box>
            <Tooltip title={description ?? ''} placement="right" disableHoverListener={!description}>
                <ListItemButton
                    id={`sidebar-node-${name}`}
                    onClick={() => {
                        hasChildren && setOpen((v) => !v);
                        onClick();
                    }}
                    selected={selected}
                    onDragOver={
                        onDrop
                            ? (e) => {
                                  e.preventDefault();
                                  setIsDragOver(true);
                              }
                            : undefined
                    }
                    onDragLeave={onDrop ? () => setIsDragOver(false) : undefined}
                    onDrop={
                        onDrop
                            ? (e) => {
                                  e.preventDefault();
                                  setIsDragOver(false);
                                  onDrop();
                              }
                            : undefined
                    }
                    sx={{
                        pl: 1 + depth * 2,
                        borderRadius: 1,
                        py: 0.5,
                        ...(isDragOver && { bgcolor: 'action.selected' }),
                    }}
                >
                    {hasChildren && (
                        <Box sx={{ display: 'flex', color: 'text.secondary' }}>
                            {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
                        </Box>
                    )}
                    <ListItemIcon sx={{ minWidth: 32 }}>{icon ?? <LanguageIcon fontSize="small" />}</ListItemIcon>
                    <ListItemText primary={name} slotProps={{ primary: { variant: 'body2', noWrap: true } }} />
                    {actions && (
                        <Box sx={{ ml: 'auto', flexShrink: 0 }} onClick={(e) => e.stopPropagation()}>
                            {actions}
                        </Box>
                    )}
                </ListItemButton>
            </Tooltip>

            {hasChildren && (
                <Collapse in={open} unmountOnExit>
                    <List dense disablePadding>
                        {children}
                    </List>
                </Collapse>
            )}
        </Box>
    );
}
